import { NextPageContext } from 'next';
import {
  Job, JobPayType, JobPayDisplay, JobLocationType,
} from 'lib/types/jobs';
import { JobDetail } from 'features/JobDetail';
import { JobService } from 'server/api/services/jobs';
import { toNumber } from 'server/api/services/utils';
import {
  serialize, buildJobPath, jobToSlug, publicIdFromSlug,
} from 'lib/helpers';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { imageUrl } from 'lib/utils/imageUtils';
import { redirectTo404 } from 'lib/constants';
import { getPayInfo } from 'lib/utils/jobs';
import { jobLocationTypes } from 'lib/constants/jobs';
import { compact } from 'lodash';
import { HotjarHead } from 'components/HotjarHead';
import { getUserIdFromContext } from 'server/api/services/auth';
import { getStructuredJobData } from 'server/api/services/jobs/utils';
import { UserListsProvider } from 'lib/contexts/userListsContext';

export const getServerSideProps = async (context: NextPageContext) => {
  const { query } = context;
  const { jobId } = query;

  if (!jobId) {
    return redirectTo404;
  }

  try {
    const currentUserId = await getUserIdFromContext(context);
    const publicId = publicIdFromSlug(jobId as string);

    const job = await JobService.fetchJobByPublicId(publicId, currentUserId);

    if (!job) {
      const j = await JobService.fetchJobById(toNumber(jobId), currentUserId);
      if (j) {
        return {
          redirect: {
            destination: buildJobPath(j),
            permanent: true,
          },
        };
      }
      return redirectTo404;
    }

    const serializedJob = serialize(job);
    const slug = jobToSlug(serializedJob);

    if (jobId !== slug) {
      return {
        redirect: {
          destination: buildJobPath(serializedJob),
          permanent: true,
        },
      };
    }

    if (serializedJob.isPrivate && !currentUserId) {
      return {
        redirect: {
          destination: `/signin?returnTo=${encodeURIComponent(buildJobPath(serializedJob))}`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        job: serializedJob,
      },
    };
  } catch (err) {
    return redirectTo404;
  }
};

interface Props {
  job: Job;
}

const JobDetailPage = ({ job }: Props) => {
  const jobTitle = `${job.name} at ${job.account.name}`;
  const locationType = jobLocationTypes[job.locationType as JobLocationType];
  const locations = job.locations?.map((l) => l.name).join('/');
  const payInfo = getPayInfo(
    job.payType as JobPayType,
    job.payDisplay as JobPayDisplay,
    job.payAmount,
    job.payMin,
    job.payMax,
  );
  const jobDescription = compact([locationType, locations, payInfo]).join(' | ');

  const structuredData = getStructuredJobData(job, jobDescription);

  return (
    <>
      <GondolaHead
        title={jobTitle}
        description={jobDescription}
        url={`https://gondola.cc${buildJobPath(job)}`}
        image={imageUrl(job.cardUrl)}
        cardUrl={imageUrl(job.cardUrl)}
        structuredData={structuredData}
        robots={job.isPrivate ? 'noindex,nofollow' : ''}
      />
      <HotjarHead />
      <UserListsProvider>
        <JobDetail job={job} />
      </UserListsProvider>
    </>
  );
};

export default JobDetailPage;
